import React, { useMemo } from 'react';
import { useSensorsChartByModelQuery, useSensorsDataQuery } from 'queries/sensors';
import { parseAssetTags } from 'pages/Asset/tabs/Events/components/ChartsArea/helpers';
import useSensorsTags from './hooks/useSensorsTags';
import {
  useChartVariables,
  LineChart,
  ChartFilters,
  ChartLegend,
} from '@shape/lighthouse-components';
import '../style.scss';
import { buildTagsConfig } from '../helpers/helper';
import { useInterpolationData } from '../hooks/useInterpolationData';

type SensorsByAsset = {
  tagsBy: 'asset';
  modelName?: undefined;
  eventId?: undefined;
};
type SensorsByEvent = {
  tagsBy: 'event';
  eventId: string;
  modelName?: undefined;
};
type SensorsByModel = {
  tagsBy: 'model';
  modelName: string;
  eventId?: undefined;
};
type SensorsChartsProps = DefaultChartComponentProps &
  (SensorsByAsset | SensorsByEvent | SensorsByModel);

const SensorsCharts: React.FC<SensorsChartsProps> = ({
  tagsBy,
  showExcludeGap,
  enableCustomRange,
  assetId,
  alarmsInfo,
  overrideGraphicOptions,
  children,
  modelName,
  hasLegendCategoryFilter,
  showDefaultLegend = true,
  eventId,
  customPeriodOptions,
}) => {
  const { tags, isLoading } = useSensorsTags(tagsBy, assetId, modelName, eventId);
  const { dateRange, selectedChartVariables } = useChartVariables();
  const tagsConfig = useMemo(
    () => buildTagsConfig(parseAssetTags(tags)),
    [tags, tagsBy, modelName, assetId],
  );

  const window = useInterpolationData(dateRange);

  const chartQuery = tagsBy === 'model' ? useSensorsChartByModelQuery : useSensorsDataQuery;

  const { data, isFetching, isError } = chartQuery(
    {
      assetId,
      modelName,
      startDate: dateRange.start,
      endDate: dateRange.end,
      tags: selectedChartVariables.filter((tag) => tagsConfig.has(tag)),
      window,
    },
    {
      enabled: !!window && selectedChartVariables.filter((tag) => tagsConfig.has(tag)).length > 0,
    },
  );

  return (
    <>
      <ChartFilters
        showExcludeGap={showExcludeGap}
        enableCustomRange={enableCustomRange}
        hasAlarmPeriod={!!alarmsInfo}
        hasLegendCategory={hasLegendCategoryFilter}
        tags={tags?.categories}
        customPeriodOptions={customPeriodOptions}
      />

      {children}

      <LineChart
        chartData={data?.sensors ?? []}
        tagsConfig={tagsConfig}
        isFetching={isFetching || isLoading}
        isError={isError}
        alarmsInfo={alarmsInfo}
        overrideGraphicOptions={overrideGraphicOptions}
      />

      {showDefaultLegend && <ChartLegend hasLegendCategoryFilter />}
    </>
  );
};

export default SensorsCharts;
